import React from 'react'
import { Button } from '../ui/Button'
import { useI18n } from '../../i18n'
import { featureFlags } from '../../config/featureFlags'
import { GovernanceListingSection } from '../governance/GovernanceListingSection'
import wfStyles from '@/styles/pages/compliance-workflow/ComplianceWorkflow.module.scss'

type DiscoveryControl = {
  controlCode?: string
  canonicalControlKey?: string
  controlTitle?: string
  soc2Mapping?: string | null
  applicabilityReason?: string | null
  expectedEvidenceCount?: number
  expectedEvidence?: Array<{ title: string }>
  inherited?: boolean
  scoped?: boolean
}

interface DiscoveryItem {
  informationSystemId: string
  systemName?: string | null
  applicableCount: number
  organizationControls: number
  scopedControls: number
  inheritedControls: number
  controls?: DiscoveryControl[]
  technicalDrivers?: Array<{ controlCode: string; controlTitle?: string; isTechnicalDriver?: boolean }>
  sampleExplanations?: Array<{ type: string; message: string }>
}

interface Props {
  discoveries?: DiscoveryItem[]
  onDiscover: () => void
  loading?: boolean
  scopeRevision?: number
  staleDiscovery?: boolean
}

export default function ApplicabilityDiscoveryPanel({
  discoveries,
  onDiscover,
  loading,
  scopeRevision,
  staleDiscovery,
}: Props) {
  const { t } = useI18n()
  const canonicalV1 = featureFlags.CANONICAL_CONTROL_LIBRARY_V1
  const scopeAuthorityV1 = featureFlags.COMPLIANCE_SCOPE_AUTHORITY_V1

  return (
    <GovernanceListingSection
      title={t('complianceWorkflow.applicabilityDiscovery', 'Applicability Discovery')}
      description={
        scopeAuthorityV1
          ? t(
              'complianceWorkflow.applicabilityDiscoveryScopedHint',
              'Scoped systems only — controls discovered for workflow-selected information systems.',
            )
          : t(
              'complianceWorkflow.applicabilityDiscoveryHint',
              'Discover controls from topology engine with explanations.',
            )
      }
      toolbar={
        <Button type="button" size="small" color="brand" disabled={loading} onClick={onDiscover}>
          {t('complianceWorkflow.runDiscovery', 'Run discovery')}
        </Button>
      }
    >
      {scopeAuthorityV1 && scopeRevision != null ? (
        <p className={wfStyles.itemMeta}>
          {t('complianceWorkflow.scopedSystemsOnly', 'Scoped systems only')} ·{' '}
          {t('complianceWorkflow.scopeRevision', 'revision')} {scopeRevision}
        </p>
      ) : null}
      {staleDiscovery ? (
        <p className={wfStyles.itemMeta}>
          {t('complianceWorkflow.scopeChangedRerunDiscovery', 'Scope changed. Run discovery again.')}
        </p>
      ) : null}
      {discoveries?.length ? (
        <ul className={wfStyles.innerStack}>
          {discoveries.map((d) => (
            <li key={d.informationSystemId} className={wfStyles.listItem}>
              <p className={wfStyles.itemTitle}>
                {d.systemName || `${d.informationSystemId.slice(0, 8)}…`}
              </p>
              <p className={wfStyles.itemMeta}>
                {d.applicableCount} {t('complianceTopology.applicability', 'applicable')} ·{' '}
                {d.inheritedControls} {t('complianceTopology.inheritedControls', 'Inherited')} ·{' '}
                {d.scopedControls} {t('complianceTopology.scopedControls', 'scoped')}
              </p>
              {canonicalV1 && d.controls?.length ? (
                <ul className={`${wfStyles.innerStack} mt-2`}>
                  {d.controls.slice(0, 12).map((c) => (
                    <li key={c.canonicalControlKey || c.controlCode} className={wfStyles.listItem}>
                      <p className={wfStyles.itemTitle}>
                        {c.canonicalControlKey || c.controlCode} — {c.controlTitle || c.controlCode}
                      </p>
                      {c.soc2Mapping ? (
                        <p className={wfStyles.itemMeta}>
                          {t('canonicalLibrary.soc2Mapping', 'SOC 2 mapping')}: {c.soc2Mapping}
                        </p>
                      ) : null}
                      {c.applicabilityReason ? (
                        <p className={wfStyles.itemMeta}>{c.applicabilityReason}</p>
                      ) : null}
                      {c.expectedEvidenceCount != null && c.expectedEvidenceCount > 0 ? (
                        <p className={wfStyles.itemMeta}>
                          {t('canonicalLibrary.expectedEvidence', 'Expected evidence')}:{' '}
                          {c.expectedEvidence?.slice(0, 3).map((e) => e.title).join(', ') ||
                            c.expectedEvidenceCount}
                        </p>
                      ) : null}
                      {c.inherited ? (
                        <p className={wfStyles.itemMeta}>
                          {t('canonicalLibrary.inheritedOrganizationControl', 'Inherited organization control')}
                        </p>
                      ) : c.scoped ? (
                        <p className={wfStyles.itemMeta}>
                          {t('canonicalLibrary.scopedSystemControl', 'Scoped system control')}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                d.sampleExplanations?.slice(0, 2).map((ex, i) => (
                  <p key={i} className={wfStyles.itemMeta} title={ex.message}>
                    {ex.message}
                  </p>
                ))
              )}
              {canonicalV1 && d.technicalDrivers?.length ? (
                <details className="mt-2">
                  <summary className={wfStyles.itemMeta}>
                    {t('canonicalLibrary.technicalApplicabilityDriver', 'Technical applicability drivers')}
                  </summary>
                  <ul className={wfStyles.innerStack}>
                    {d.technicalDrivers.map((td) => (
                      <li key={td.controlCode} className={wfStyles.itemMeta}>
                        {td.controlCode}
                        {td.controlTitle ? ` — ${td.controlTitle}` : ''}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className={wfStyles.loadingText}>
          {t('complianceWorkflow.runDiscoveryHint', 'Run discovery to populate applicability results.')}
        </p>
      )}
    </GovernanceListingSection>
  )
}
